import React, { createContext, useEffect, useState } from "react";
import { onAuthStateChanged, sendPasswordResetEmail, signOut } from "firebase/auth";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import { getCurrentUser } from "../../authentication/hooks/AuthHooks";

export const AuthContext = createContext({
  isLoggedIn: null,
  onLogin: () => {},
  onLogout: () => {},
  forgotPassword: (email) => {},
  currentAuth: null,
  currentUser: null,
  getCurrentUser: () => {},
  getUserData: () => {},
  currentLayoutAccess: null,
  organizationId: null,
  setCurrentUser: () => {},
  setCurrentLayoutAccess: () => {},
  setOrganizationId: () => {},
  navigationRoute: null,
  setNavigationRoute: () => {},
  navigationStateData: null,
  setNavigationStateData: () => {},
});

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentAuth, setCurrentAuth] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentLayoutAccess, setCurrentLayoutAccess] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [navigationRoute, setNavigationRoute] = useState(null);
  const [navigationStateData, setNavigationStateData] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  
  // useEffect(() => {
    //   const unsubscribe = onAuthStateChanged(auth, (user) => {
      //     if (user) {
        //       setCurrentAuth(user);
  //       getUserData(); // Make sure user data is fetched
  //     } else {
  //       // Handle logged out state
  //       setIsLoggedIn(false);
  //       setCurrentUser(null);
  //       localStorage.clear(); // Clear local storage if needed
  //     }
  //   });
  
  //   return unsubscribe; // Clean up the listener on unmount
  // }, []);

  // Auth Listener - handle authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Firebase auth state changed:", user ? "User logged in" : "User logged out");
      
      if (user) {
        // User is authenticated, fetch user data
        try {
          const querySnap = await getCurrentUser();

          if (querySnap.docs && querySnap.docs.length > 0) {
            const userData = querySnap.docs[0].data();
            const userId = querySnap.docs[0].id;
            
            const userObject = {
              ...userData,
              id: userId,
            };
            
            // Set both currentUser and isLoggedIn simultaneously to prevent race condition
            setCurrentUser(userObject);
            setIsLoggedIn(true);
            console.log("User data loaded:", userData);
            sessionStorage.setItem("adminType", userData.type);
            // Set organizationId for Organization Admin users
            if (userData.organization) {
              console.log("Setting organizationId:", userData.organization);
              setOrganizationId(userData.organization);
              localStorage.setItem("organizationId", userData.organization);
            }
            
            //sessionStorage.setItem('notificationsLastViewedTime', userData.lastLoginDate);
            //const tempNot = sessionStorage.getItem('notificationsLastViewedTime');
            //console.log("notificationsLastViewedTime:", tempNot);
            // Update lastLoginDate in Firestore
            try {
              const adminDocRef = doc(db, "admins", userId);
              await updateDoc(adminDocRef, {
                lastLoginDate: serverTimestamp()
              });
              console.log("Updated lastLoginDate for user:", userId);
            } catch (updateError) {
              console.error("Error updating lastLoginDate:", updateError);
            }

            // Navigate to stored route (deep link or default) - this is the final step
            let finalRoute = sessionStorage.getItem('finalRoute') || localStorage.getItem('finalRoute');
            
            // If finalRoute is invalid or not set, determine the correct default route
            if (!finalRoute || finalRoute === '/' || finalRoute === '/login') {
              finalRoute = userData.type === 'organization'
                ? '/home'
                : '/general-admin-dashboard';
            }
            
            console.log("Current User:", userData);
            console.log('Authentication complete, navigating to:', finalRoute);
            navigate(finalRoute);
            
            // Clear the stored route
            sessionStorage.removeItem('finalRoute');
            localStorage.removeItem('finalRoute');
          } else {
            console.log("No admin user found in database");
            setCurrentUser(false);
            setIsLoggedIn(false);
          }
        } catch (error) {
          console.log("Error getting user data:", error);
          setCurrentUser(false);
          setIsLoggedIn(false);
        }
      } else {
        // User is not authenticated
        console.log("User logged out, clearing state");
        setCurrentUser(false);
        setIsLoggedIn(false);
        setCurrentLayoutAccess(null);
        setOrganizationId(null);
      }
      
      setAuthInitialized(true);
    });

    return unsubscribe;
  }, []);

  // useEffect(() => {
  //   // Listen to changes in Firebase Authentication state
  //   const unsubscribe = onAuthStateChanged(auth, (user) => {
  //     if (user) {
  //       // User is logged in, set auth state
  //       setCurrentAuth(user);
  //     } else {
  //       // User is not logged in, set auth state to null
  //       setCurrentAuth(null);
  //       setIsLoggedIn(false);
  //       setCurrentUser(null);
  //     }
  //   });
  
  //   // Cleanup the listener when the component is unmounted
  //   return unsubscribe;
  // }, []);
  
  // useEffect(() => {
  //   // Fetch user data only if the user is logged in and `currentAuth` exists
  //   if (currentAuth) {
  //     getUserData();
  //   } else {
  //     setCurrentUser(null);
  //   }
  // }, [currentAuth, isLoggedIn]);

  // getUserData function removed - now handled directly in onAuthStateChanged
  // isLoggedIn is now set directly in onAuthStateChanged to prevent race conditions

  const forgotPassword = (email) => {
    console.log("emailfor", email);
    sendPasswordResetEmail(auth, email)
      .then(() => {
        console.log("Password reset email sent!");
        // Password reset email sent!
        // ..
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        console.log(errorCode, errorMessage);
        // ..
      });
  };

  const onLogin = () => {
    console.log("inside on login");
    // Remove localStorage manipulation - let Firebase handle auth state
    // setIsLoggedIn and localStorage will be managed by the auth state flow
  };

  const onLogout = () => {
    console.log("inside on logout");
    signOut(auth);
    // Don't manually set states here - let the Firebase auth listener handle it
    localStorage.clear();
    sessionStorage.clear();
  };

  // useEffect(() => {
  //   console.log("changing", isLoggedIn);
  // }, [isLoggedIn]);

  useEffect(() => {
    if (localStorage.getItem("organizationId")) {
      setOrganizationId(localStorage.getItem("organizationId"));
    }
    if (localStorage.getItem("currentLayoutAccess")) {
      setCurrentLayoutAccess(localStorage.getItem("currentLayoutAccess"));
    }
    if (localStorage.getItem("navigationRoute")) {
      setNavigationRoute(localStorage.getItem("navigationRoute"));
    }
    if (localStorage.getItem("navigationStateData")) {
      setNavigationStateData(localStorage.getItem("navigationStateData"));
    }
  }, []);

  const handleUpdateProfile = async () => {
    try {
      if (currentUser) {
        // Update the user's profile
        await currentUser.updateProfile({
          displayName: "New Display Name",
          // Other fields you want to update
        });

        // Since currentUser is already updated, you don't need to do anything here
        console.log("User profile updated successfully");
      } else {
        console.error("No current user available");
      }
    } catch (error) {
      console.error("Error updating user profile:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        onLogin,
        onLogout,
        forgotPassword,
        getCurrentUser,
        currentAuth,
        setCurrentAuth,
        currentUser,
        setCurrentUser,
        currentLayoutAccess,
        setCurrentLayoutAccess,
        organizationId,
        setOrganizationId,
        navigationRoute,
        setNavigationRoute,
        navigationStateData,
        setNavigationStateData,
        authInitialized,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
